import { completePyqTrendData } from "./data";
import type { ChapterTrend, ExamId, SubjectId } from "./types";

/** Derived helpers. Every value comes from the dataset — nothing is invented. */

export const EXAM_SUBJECTS: Record<ExamId, SubjectId[]> = {
  jee: subjectsWithData("jee"),
  neet: subjectsWithData("neet"),
};

function subjectsWithData(exam: ExamId): SubjectId[] {
  const group = completePyqTrendData[exam] ?? {};
  return (Object.keys(group) as SubjectId[]).filter((s) => (group[s]?.length ?? 0) > 0);
}

export const SUBJECT_LABEL: Record<SubjectId, string> = {
  physics: "Physics",
  chemistry: "Chemistry",
  biology: "Biology",
  math: "Mathematics",
};


export const EXAM_LABEL: Record<ExamId, string> = { jee: "JEE", neet: "NEET" };

export function getChapters(exam: ExamId, subject: SubjectId): ChapterTrend[] {
  return completePyqTrendData[exam]?.[subject] ?? [];
}

/* ---------------------------------- trend --------------------------------- */

export type TrendDirection = "increasing" | "decreasing" | "stable" | "repeated";

/** Compares the newer half of recentTrend with the older half. */
export function trendDirection(recentTrend: number[]): TrendDirection {
  const points = recentTrend.filter((n) => Number.isFinite(n));
  if (points.length < 2) return "stable";
  const mid = Math.floor(points.length / 2);
  const older = points.slice(0, mid);
  const newer = points.slice(points.length - mid);
  const avg = (list: number[]) => list.reduce((s, n) => s + n, 0) / (list.length || 1);
  const delta = avg(newer) - avg(older);
  const unique = new Set(points).size;
  if (Math.abs(delta) < 0.5) return unique === 1 ? "repeated" : "stable";
  return delta > 0 ? "increasing" : "decreasing";
}

export const TREND_LABEL: Record<TrendDirection, string> = {
  increasing: "Increasing",
  decreasing: "Decreasing",
  stable: "Stable",
  repeated: "Frequently repeated",
};

/* --------------------------------- priority -------------------------------- */

export type PriorityId = "very-high" | "high" | "medium" | "low";

export const PRIORITIES: { id: PriorityId; label: string; emoji: string; className: string }[] = [
  {
    id: "very-high",
    label: "Very High Priority",
    emoji: "🔥",
    className: "border-orange-400/30 bg-orange-400/10 text-orange-600 dark:text-orange-300",
  },
  {
    id: "high",
    label: "High Priority",
    emoji: "🟢",
    className: "border-emerald-400/30 bg-emerald-400/10 text-emerald-600 dark:text-emerald-300",
  },
  {
    id: "medium",
    label: "Medium Priority",
    emoji: "🟡",
    className: "border-amber-400/30 bg-amber-400/10 text-amber-600 dark:text-amber-300",
  },
  {
    id: "low",
    label: "Low Priority",
    emoji: "⚪",
    className: "border-white/15 bg-surface text-muted-foreground",
  },
];

export const PRIORITY_BY_ID = new Map(PRIORITIES.map((p) => [p.id, p]));

/** Reads the first number in strings like "85%" or "High (80-90%)". */
export function probabilityValue(repeatProbability: string): number | null {
  const match = String(repeatProbability ?? "").match(/\d+(\.\d+)?/);
  if (match) return Number(match[0]);
  const text = String(repeatProbability ?? "").toLowerCase();
  if (text.includes("very high")) return 90;
  if (text.includes("high")) return 75;
  if (text.includes("medium") || text.includes("moderate")) return 50;
  if (text.includes("low")) return 25;
  return null;
}

function yieldRank(yieldCategory: string): number {
  // Categories read "<yield> Yield / <effort> Effort" — only the yield half counts.
  const text = String(yieldCategory ?? "").toLowerCase().split("/")[0];
  if (text.includes("very high")) return 3;
  if (text.includes("high")) return 2;
  if (text.includes("medium") || text.includes("moderate")) return 1;
  return 0;
}

/**
 * Combines the dataset's own signals (yieldCategory, repeatProbability and the
 * recentTrend direction) into a single preparation priority.
 */
export function chapterPriority(chapter: ChapterTrend): PriorityId {
  const yieldScore = yieldRank(chapter.yieldCategory);
  const probability = probabilityValue(chapter.repeatProbability) ?? 0;
  const probabilityScore = probability >= 80 ? 3 : probability >= 60 ? 2 : probability >= 40 ? 1 : 0;
  const direction = trendDirection(chapter.recentTrend);
  const trendScore = direction === "increasing" || direction === "repeated" ? 1 : 0;
  const total = yieldScore + probabilityScore + trendScore;
  if (total >= 6) return "very-high";
  if (total >= 4) return "high";
  if (total >= 2) return "medium";
  return "low";
}

/* --------------------------------- summary --------------------------------- */

export type PyqSummary = {
  totalChapters: number;
  highYield: number;
  avgQuestions: number;
  avgMarks: number;
  topProbability: { label: string; chapter: string } | null;
  topChapters: ChapterTrend[];
};

export function summarise(chapters: ChapterTrend[]): PyqSummary {
  const total = chapters.length;
  const sum = (pick: (c: ChapterTrend) => number) =>
    chapters.reduce((acc, c) => acc + (Number(pick(c)) || 0), 0);
  const highYield = chapters.filter((c) => yieldRank(c.yieldCategory) >= 2).length;

  let topProbability: PyqSummary["topProbability"] = null;
  let best = -1;
  for (const c of chapters) {
    const value = probabilityValue(c.repeatProbability);
    if (value !== null && value > best) {
      best = value;
      topProbability = { label: c.repeatProbability, chapter: c.chapter };
    }
  }

  const order: PriorityId[] = ["very-high", "high", "medium", "low"];
  const topChapters = [...chapters]
    .sort((a, b) => {
      const diff = order.indexOf(chapterPriority(a)) - order.indexOf(chapterPriority(b));
      return diff !== 0 ? diff : b.avgQuestions - a.avgQuestions;
    })
    .slice(0, 5);

  return {
    totalChapters: total,
    highYield,
    avgQuestions: total ? Math.round((sum((c) => c.avgQuestions) / total) * 10) / 10 : 0,
    avgMarks: total ? Math.round((sum((c) => c.avgMarks) / total) * 10) / 10 : 0,
    topProbability,
    topChapters,
  };
}

/* ----------------------------- filter & sorting ---------------------------- */

export type PyqSort = "questions" | "marks" | "probability" | "az";

export const SORTS: { id: PyqSort; label: string }[] = [
  { id: "questions", label: "Avg questions" },
  { id: "marks", label: "Avg marks" },
  { id: "probability", label: "Repeat probability" },
  { id: "az", label: "A–Z" },
];

export function filterChapters(
  chapters: ChapterTrend[],
  opts: { query: string; classLevel: "all" | "11th" | "12th"; priority: "all" | PriorityId },
): ChapterTrend[] {
  const q = opts.query.trim().toLowerCase();
  return chapters.filter((c) => {
    if (opts.classLevel !== "all" && c.classLevel !== opts.classLevel) return false;
    if (opts.priority !== "all" && chapterPriority(c) !== opts.priority) return false;
    if (!q) return true;
    return (
      c.chapter.toLowerCase().includes(q) ||
      c.yieldCategory?.toLowerCase().includes(q) ||
      c.subTopics?.some((s) => s.name?.toLowerCase().includes(q))
    );
  });
}

export function sortChapters(chapters: ChapterTrend[], sort: PyqSort): ChapterTrend[] {
  const list = [...chapters];
  switch (sort) {
    case "marks":
      return list.sort((a, b) => b.avgMarks - a.avgMarks);
    case "probability":
      return list.sort(
        (a, b) =>
          (probabilityValue(b.repeatProbability) ?? -1) -
          (probabilityValue(a.repeatProbability) ?? -1),
      );
    case "az":
      return list.sort((a, b) => a.chapter.localeCompare(b.chapter));
    default:
      return list.sort((a, b) => b.avgQuestions - a.avgQuestions);
  }
}
